// Pure pieces of rescheduling an appointment ("anytime") booking: the
// pricing options the shared appointment picker maps slots onto, the month
// it asks the API about, and the instant the chosen slot starts at. A port of
// v1's getPricingOptions + buildRescheduleSession, on Intl rather than moment.

export type AppointmentPricingOption = {
  id: string;
  name: string;
  description?: string;
  price: number;
  duration?: number;
  ticketsIds: string[];
};

import type { ApiTicketType as TicketTypeLike, ScheduleSlot } from "./apiTypes";

/** The listing's configured slots: group-schedule listings keep them apart. */
export function scheduleSlotsOf(course: { isGroupSchedule?: boolean; appointmentScheduleSlots?: unknown; groupScheduleSlots?: unknown }) {
  const slots = course.isGroupSchedule ? course.groupScheduleSlots : course.appointmentScheduleSlots;
  return Array.isArray(slots) ? (slots as ScheduleSlot[]) : [];
}

/** Slots' tickets grouped into pricing options: tickets sharing a name,
 * description, price and duration are one option (v1's getPricingOptions),
 * cheapest and shortest first. A ticket whose type is gone is left out
 * (v1 threw on one). */
export function buildPricingOptions(slots: ScheduleSlot[], ticketTypes: TicketTypeLike[] | undefined): AppointmentPricingOption[] {
  if (!ticketTypes?.length) return [];
  const options: AppointmentPricingOption[] = [];
  for (const slot of slots) {
    if (slot.status !== "enabled") continue;
    for (const ticket of slot.tickets ?? []) {
      const ticketTypeId = ticket.ticketTypes?.[0];
      const ticketType = ticketTypes.find((t) => String(t._id) === String(ticketTypeId));
      if (!ticketType) continue;
      const price = Number(ticketType.price ?? 0);
      let option = options.find(
        (o) => o.name === (ticketType.name ?? "") && o.description === ticketType.description && o.price === price && o.duration === ticket.duration,
      );
      if (!option) {
        option = {
          id: String(ticketType._id),
          name: ticketType.name ?? "",
          description: ticketType.description,
          price,
          duration: ticket.duration,
          ticketsIds: [],
        };
        options.push(option);
      }
      if (ticket._id != null) option.ticketsIds.push(String(ticket._id));
    }
  }
  return options.sort((a, b) => a.price - b.price || (a.duration ?? 0) - (b.duration ?? 0));
}

const pad = (n: number) => String(n).padStart(2, "0");

/** A calendar day as "YYYY-MM-DD", read from the Date's local fields (what
 * the picker's calendar hands back). */
export function localDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The day an instant falls on in `timezone`, as "YYYY-MM-DD". */
export function dateKeyInZone(iso: string, timezone: string): string {
  const p: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat("en-GB", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(iso))) {
    p[part.type] = part.value;
  }
  return `${p.year}-${p.month}-${p.day}`;
}

/** The first and last day of the month a Date is in, as the
 * available-slots endpoint takes them. */
export function monthRange(month: Date): { startDate: string; endDate: string } {
  const y = month.getFullYear();
  const m = month.getMonth();
  const last = new Date(y, m + 1, 0).getDate();
  return { startDate: `${y}-${pad(m + 1)}-01`, endDate: `${y}-${pad(m + 1)}-${pad(last)}` };
}

function offsetMinutes(utcMs: number, timezone: string): number {
  const p: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(utcMs))) {
    p[part.type] = part.value;
  }
  const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
  return Math.round((asUtc - utcMs) / 60_000);
}

/** The instant a wall-clock time on a day in `timezone` is: "2026-03-05",
 * 870 (14:30), "Europe/London" → "2026-03-05T14:30:00.000Z". */
export function zonedWallTimeToUtc(dateKey: string, minutes: number, timezone: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const wall = Date.UTC(y!, m! - 1, d!, Math.floor(minutes / 60), minutes % 60);
  // Two passes settle the offset either side of a DST change.
  let utc = wall - offsetMinutes(wall, timezone) * 60_000;
  utc = wall - offsetMinutes(utc, timezone) * 60_000;
  return new Date(utc).toISOString();
}

export type AvailableDate = {
  dateISO: string;
  isDisabled?: boolean;
  availableSlots?: { startMinutes: number; endMinutes: number; startUTC?: string }[];
};

/** The one zone the picker builds its days in and reads the chosen day
 * back in: the slots answer's (the zone it states the minutes in), else the
 * listing's. Using one for the days and another for the lookup finds no
 * day, or the wrong one, when they differ. */
export function appointmentTimezone(slotsTimezone: string | undefined | null, courseTimezone: string): string {
  return slotsTimezone || courseTimezone;
}

/** When the chosen slot starts: the API's own instant for it, else the day
 * and minutes read in the listing's zone (the zone the API states the
 * minutes in). Null until both a day and a time are chosen. */
export function resolveAppointmentStart(args: {
  availableDates: AvailableDate[];
  date: Date | null;
  time: number | null;
  timezone: string;
}): { startDateTime: string; duration?: number } | null {
  if (!args.date || args.time == null) return null;
  const key = localDateKey(args.date);
  const day = args.availableDates.find((d) => dateKeyInZone(d.dateISO, args.timezone) === key);
  const slot = day?.availableSlots?.find((s) => s.startMinutes === args.time);
  const duration = slot && slot.endMinutes > slot.startMinutes ? slot.endMinutes - slot.startMinutes : undefined;
  if (slot?.startUTC) return { startDateTime: new Date(slot.startUTC).toISOString(), duration };
  return { startDateTime: zonedWallTimeToUtc(key, args.time, args.timezone), duration };
}
